import {
  useCallback,
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
} from "react";
import { CellPosition } from "../../../internal/types";
import { EditableCellImperativeHandle, EditableCellCellProps } from "./types";
import { useDTFocusDispatchContext } from "../context/focus/useDTFocusDispatchContext";

type UseEditableMenuCellOptions = {
  cellPosition: CellPosition;
  onCellKeyDown: EditableCellCellProps["onKeyDown"];
  ref: React.Ref<EditableCellImperativeHandle>;
};

/**
 * Shared hook for select/multiselect editable DataTable cells that use a menu-based
 * editing UX (SelectMenu, MultiSelectMenu, and their async variants).
 *
 * Encapsulates the common state, refs, imperative handle, keyboard/focus/mouse handlers,
 * and focus context value that are identical across all four menu-based cell components.
 *
 * @param options.cellPosition - The position of the cell in the table grid
 * @param options.onCellKeyDown - The cell's keyDown handler from cellProps, forwarded when the menu is closed
 * @param options.ref - Ref for the imperative handle (invoke/focus)
 * @returns Shared state, refs, event handlers, and cellFocusContextValue
 */
export function useEditableMenuCell({
  cellPosition,
  onCellKeyDown,
  ref,
}: UseEditableMenuCellOptions) {
  const [tabIndex, setTabIndex] = useState(-1);
  const { moveFocusToCell } = useDTFocusDispatchContext();
  const cellRef = useRef<HTMLTableCellElement>(null);
  const triggerClickRef = useRef<(() => void) | null>(null);

  useImperativeHandle(ref, () => ({
    invoke: () => {
      triggerClickRef.current?.();
    },
    focus: () => {
      cellRef.current?.focus();
      setTabIndex(0);
    },
  }));

  const handleCellKeyDown = useCallback(
    (
      event: React.KeyboardEvent<HTMLElement>,
      menuKeyDown:
        | ((event: React.KeyboardEvent<HTMLButtonElement>) => void)
        | undefined,
      isOpen: boolean,
    ) => {
      if (isOpen) {
        menuKeyDown?.(event as React.KeyboardEvent<HTMLButtonElement>);
        return;
      }
      onCellKeyDown?.(event as React.KeyboardEvent<HTMLDivElement>);
    },
    [onCellKeyDown],
  );

  // F2 while menu is open: close it and restore focus to the cell (explicit close)
  const handleMenuKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "F2") {
      e.preventDefault();
      triggerClickRef.current?.();
    }
  }, []);

  // Tab/click-outside: menu closed implicitly, reset tabIndex since handleCellBlur won't re-fire
  const handleImplicitClose = useCallback(() => {
    setTabIndex(-1);
  }, []);

  const handleCellMouseDown = useCallback(
    (triggerOnClick: () => void) => () => {
      moveFocusToCell(cellPosition);
      triggerOnClick();
    },
    [cellPosition, moveFocusToCell],
  );

  const handleCellBlur = useCallback((e: React.FocusEvent<HTMLElement>) => {
    const popoverId = (e.currentTarget as HTMLElement).getAttribute(
      "aria-controls",
    );
    const popoverEl = popoverId ? document.getElementById(popoverId) : null;
    if (popoverEl?.contains(e.relatedTarget as Node)) {
      return;
    }
    setTabIndex(-1);
  }, []);

  const handleCellFocus = useCallback(
    (event: React.FocusEvent<HTMLTableCellElement>) => {
      if (event.target !== cellRef.current) {
        return;
      }
      event.preventDefault();
      setTabIndex(0);
    },
    [],
  );

  const cellFocusContextValue = useMemo(
    () => ({ isCellFocused: tabIndex === 0 }),
    [tabIndex],
  );

  return {
    tabIndex,
    cellRef,
    triggerClickRef,
    handleCellKeyDown,
    handleMenuKeyDown,
    handleImplicitClose,
    handleCellMouseDown,
    handleCellBlur,
    handleCellFocus,
    cellFocusContextValue,
  };
}
